/**
 * PHARMAKON — Cross-module X · stock availability (brief §14, §15), browser twin of the server CONFIG.
 *
 * The block dialog, the lot cards and the Stock & lots rows (XWP-13, XWP-15) label block motifs
 * and enforce the « Autre » précision rule client-side for immediate feedback; the server
 * (`apps/server/src/services/stock-lot-ledger/blocage-lot.service.js`) remains the authority.
 *
 * Owner of the values: `apps/server/src/config/disponibilite.config.js`. This file is a twin, not
 * an import, because the API image is built from apps/server alone. The server test
 * `src/__tests__/disponibilite.parity.test.js` imports both and fails on any drift — change a
 * value there first, then here.
 */

/** XD-10 — block motifs, keyed by the Prisma enum `MotifBlocageLot`. */
export const BLOCAGE_LOT_MOTIFS = Object.freeze({
  CONTROLE_QUALITE: Object.freeze({ libelle: "Contrôle qualité", precision_requise: false }),
  RAPPEL_LOT: Object.freeze({ libelle: "Rappel de lot", precision_requise: false }),
  LITIGE_FOURNISSEUR: Object.freeze({ libelle: "Litige fournisseur", precision_requise: false }),
  RESERVE_CLIENT: Object.freeze({ libelle: "Réservé pour un client", precision_requise: false }),
  RESERVE_INTERNE: Object.freeze({ libelle: "Usage interne", precision_requise: false }),
  AUTRE: Object.freeze({ libelle: "Autre", precision_requise: true }),
});

export const BLOCAGE_LOT_MOTIF_VALUES = Object.freeze(Object.keys(BLOCAGE_LOT_MOTIFS));

/** Lifecycle of a block (Prisma enum `StatutBlocageLot`). */
export const STATUT_BLOCAGE_LOT = Object.freeze({ ACTIF: "ACTIF", LEVE: "LEVE" });

/** Column bounds the dialog enforces before sending (same as the server validator). */
export const BLOCAGE_LOT_LIMITES = Object.freeze({
  precision_max: 500,
  motif_levee_max: 500,
  quantite_max: 1_000_000,
  historique_max: 200,
});

/** Why a product reads « Non disponible » on a sales channel although it may hold units. */
export const INDISPONIBILITE = Object.freeze({
  PRODUIT_BLOQUE: "PRODUIT_BLOQUE",
  VENTE_LIBRE_INTERDITE: "VENTE_LIBRE_INTERDITE",
});

/**
 * XD-50 — the product gates each sales channel applies (server `CANAUX_DISPONIBILITE`): a gated
 * product reads « Non disponible » on that channel. The counters' offline code resolution (cross-module
 * XWP-25, `apps/web-pharmacy/src/offline/resolution-code.js`) answers with the same gates.
 */
export const CANAUX_DISPONIBILITE = Object.freeze({
  VENTE_LIBRE: Object.freeze({ statuts_vendables: Object.freeze(["ACTIF"]), bloque_vente_libre: true }),
  CREDIT: Object.freeze({ statuts_vendables: Object.freeze(["ACTIF"]), bloque_vente_libre: true }),
  CHIFA: Object.freeze({ statuts_vendables: Object.freeze(["ACTIF"]), bloque_vente_libre: false }),
  INSTANCE: Object.freeze({ statuts_vendables: Object.freeze(["ACTIF"]), bloque_vente_libre: false }),
  GESTION: Object.freeze({ statuts_vendables: null, bloque_vente_libre: false }),
});

/**
 * X XWP-98 review (XD-314 amended 2026-11-01) — the way a sale line's lot is moved (server
 * `VOIE_CHOIX_LOT`, `PATCH /caisse/fenetres/:id/lignes/:cle { via }`), each behind its own setting for
 * a CAISSIER: `LIGNE` — the line's own choice (« Le caissier choisit le lot »); `AUTRES_LOTS` — XWP-96's
 * « Sélectionner depuis autres lots ».
 */
export const VOIE_CHOIX_LOT = Object.freeze({ LIGNE: "LIGNE", AUTRES_LOTS: "AUTRES_LOTS" });

/** `Produit.statut_produit` the cashier interface never shows (server `DISPONIBILITE.statut_cache_caissier`). */
export const STATUT_PRODUIT_CACHE_CAISSIER = "BLOQUE_CACHE";
